import React from 'react';
import { Link } from 'react-router-dom';

const AiDGPrivacyPolicy: React.FC = () => {
  return (
    <section className="section">
      <div className="container">
        <div style={{ marginBottom: '2rem' }}>
          <Link 
            to="/" 
            style={{ 
              color: 'var(--accent-color)', 
              textDecoration: 'none',
              fontSize: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            ← Back to Home
          </Link>
        </div>
        
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ 
            fontSize: '2.5rem', 
            fontWeight: '700',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            AiDG - AI Dance Generator Privacy Policy
          </h1>
          <div className="section-divider"></div>
        </div>

        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto',
          lineHeight: '1.6',
          fontSize: '1rem'
        }}>
          <p style={{ marginBottom: '2rem', fontSize: '1rem' }}>
            <strong>Effective Date:</strong> March 15, 2026<br/>
            <strong>Last Updated:</strong> March 21, 2026<br/>
            <strong>Contact:</strong> rapidnova2025@gmail.com
          </p>

          <p style={{ marginBottom: '2rem' }}>
            Aidance Generator ("we", "our", or "the app") is an iOS app that lets you create dance videos from a reference photo and a reference dance (photo or video). This policy describes what data we collect, how we use it, and your choices.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>1. Information We Collect and Use</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>1.1 Content You Provide</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Photos and videos you select</strong> — When you choose a reference photo or reference video from your device (or camera), that content is sent to our servers only to perform the dance-generation service. We do not use it for advertising or unrelated purposes.</li>
            <li><strong>Generated videos</strong> — Output videos are stored on your device (and optionally in your Photos library if you save them). We may temporarily process and store inputs/outputs on our infrastructure only to deliver the feature and for limited operational purposes (e.g. debugging, improving the service).</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>1.2 Account and Usage</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Anonymous identifier</strong> — To enforce fair use (e.g. daily generation limits) and prevent abuse, we use an anonymous account identifier (Firebase Anonymous Auth). We do not require your name, email, or social login to use the app.</li>
            <li><strong>Subscription status</strong> — If you subscribe, we and our payment/subscription provider (RevenueCat and Apple) process purchase and entitlement data so we can grant you access to premium features (e.g. daily generations). We do not receive your payment card details; those are handled by Apple.</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>1.3 Device and App Integrity</h3>
          <p style={{ marginBottom: '1rem' }}>
            <strong>App Check / device attestation</strong> — We use Firebase App Check so only genuine copies of our app can call our backend. This helps us block abuse and protect your data. No personal profile is built from this for advertising.
          </p>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>1.4 Analytics and Attribution</h3>
          <p style={{ marginBottom: '2rem' }}>
            <strong>Attribution</strong> — We may use attribution and analytics services (e.g. AppSkale) to understand how users find the app and to measure app performance. These typically use device or app identifiers and usage events, not the content of your photos or videos.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>2. How We Use Your Information</h2>
          <p style={{ marginBottom: '0.5rem' }}>We use the information above to:</p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Provide and improve the dance-generation service.</li>
            <li>Enforce usage limits and subscription entitlements.</li>
            <li>Secure our systems (e.g. App Check, abuse prevention).</li>
            <li>Comply with law and protect our rights.</li>
            <li>Understand usage and performance (analytics/attribution), in line with this policy and applicable law.</li>
          </ul>
          <p style={{ marginBottom: '2rem' }}>We do not sell your photos, videos, or personal data to third parties for advertising or marketing.</p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>3. Sharing and Third Parties</h2>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>AI processing and video generation</strong> — When you generate a dance video, your reference photo and reference video are sent to our secure servers and to our AI processing partner <strong>kie.ai</strong> (kie.ai / KIE) to create your video. We obtain your explicit in-app consent before any data is sent. kie.ai processes your data solely to deliver the dance-generation feature. We choose providers under contracts that limit use to providing the service.</li>
            <li><strong>Cloud and AI providers</strong> — Photo and video you submit are processed on our infrastructure and may be sent to third-party services we use to power the generation (e.g. AI/video APIs). We choose providers under contracts that limit use to providing the service.</li>
            <li><strong>Apple</strong> — In-app purchases and subscription are processed by Apple; their privacy policy applies to that data.</li>
            <li><strong>RevenueCat</strong> — We use RevenueCat to manage subscriptions and entitlements; their privacy policy applies to data they collect and process.</li>
            <li><strong>Firebase (Google)</strong> — We use Firebase for authentication, cloud functions, and app integrity (e.g. App Check). Google's privacy policy applies to Firebase services.</li>
          </ul>
          <p style={{ marginBottom: '2rem' }}>We may disclose data if required by law, or to protect safety, rights, or property.</p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>4. Data Retention</h2>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Generation inputs and outputs</strong> — Kept only as long as needed to provide the service and for short-term operational needs (e.g. logs, debugging). We do not retain your photos or videos longer than necessary.</li>
            <li><strong>Usage and identifiers</strong> — Anonymous identifiers and usage data (e.g. for limits and analytics) may be retained for a longer period for abuse prevention and improving the service. You can request deletion of data tied to you by contacting us.</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>5. Your Choices and Rights</h2>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Photos and camera</strong> — You control which photos and videos you provide; you can revoke photo library or camera access in iOS Settings.</li>
            <li><strong>Subscriptions</strong> — Manage or cancel in iOS Subscription settings; refunds are subject to Apple's policy.</li>
            <li><strong>Deletion and access</strong> — You may contact us at <strong>rapidnova2025@gmail.com</strong> to request access, correction, or deletion of data we hold about you, subject to applicable law.</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>6. Children</h2>
          <p style={{ marginBottom: '2rem' }}>The app is not directed at children under 13 (or higher age where required). We do not knowingly collect personal data from children. If you believe a child has provided data to us, please contact us and we will delete it.</p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>7. International Users</h2>
          <p style={{ marginBottom: '2rem' }}>Our services may be provided from or use infrastructure in different countries. By using the app, you consent to transfer and processing of your data in those locations, in accordance with this policy and applicable law.</p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>8. Changes to This Policy</h2>
          <p style={{ marginBottom: '2rem' }}>We may update this privacy policy from time to time. We will post the updated policy (e.g. in the app or on a linked page) and, where required, ask for your consent. Continued use after changes means you accept the updated policy.</p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>9. Contact</h2>
          <p style={{ marginBottom: '1rem' }}>For privacy-related questions, requests, or complaints:</p>
          <p style={{ marginBottom: '2rem' }}><strong>Email:</strong> rapidnova2025@gmail.com</p>

          <hr style={{ margin: '3rem 0', border: 'none', borderTop: '1px solid #eee' }} />
          <p style={{ marginBottom: '0', fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
            This document is the privacy policy for the Aidance Generator (AiDG) iOS application.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AiDGPrivacyPolicy;
